import React, { useMemo, useState } from "react";
import { interval, sampleSizeForWidth, MIN_N } from "../lib/precision";
import { CHART_1, INK, MUTED, GRID, PAPER } from "./charts/palette";

// Interactive precision (confidence-interval width) planner. Props (starting values): method (mean, correlation,
// icc, alpha, kappa, proportion; see lib/precision.js), guess (the planning value of the estimate), sd and unit
// (mean), k (raters for icc, items for alpha), shares (kappa: how often each category is used), model (icc:
// two_way or one_way), target (the interval width wanted) with half (true: target is the ± margin), n, conf.

const W = 640, H = 260;
const M = { top: 16, right: 20, bottom: 40, left: 52 };
const PW = W - M.left - M.right, PH = H - M.top - M.bottom;

const METHODS = {
	mean: { name: "Mean", guessLabel: null, nLabel: "Sample Size", noun: "people" },
	correlation: { name: "Correlation", symbol: "r", guessLabel: "Expected Correlation", min: -0.95, max: 0.95, nLabel: "Sample Size", noun: "people" },
	icc: { name: "ICC", guessLabel: "Expected ICC", min: 0.05, max: 0.95, nLabel: "Number of Targets", noun: "targets", kLabel: "Raters per Target", kMax: 10 },
	alpha: { name: "Alpha", symbol: "α", guessLabel: "Expected Alpha", min: 0.3, max: 0.97, nLabel: "Sample Size", noun: "people", kLabel: "Items on the Scale", kMax: 40 },
	kappa: { name: "Kappa", symbol: "κ", guessLabel: "Expected Kappa", min: 0.05, max: 0.95, nLabel: "Number of Cases", noun: "cases" },
	proportion: { name: "Proportion", symbol: "p", guessLabel: "Expected Proportion", min: 0.01, max: 0.99, nLabel: "Sample Size", noun: "people" }
};

// A reliability or correlation printed without its leading zero; a mean in its own units.
const noZero = (v, places = 2) => v.toFixed(places).replace(/^(-?)0\./, "$1.");

function niceMax(value) {
	const steps = [20, 40, 60, 80, 100, 120, 160, 200, 240, 320, 400, 480, 640, 800, 1200, 1600, 2000, 3200, 4000, 6000, 8000, 12000, 20000];
	return steps.find((s) => s >= value) || 20000;
}

// Round a y-axis maximum up to a step that gives five even ticks.
function niceWidth(value) {
	const steps = [0.05, 0.1, 0.2, 0.25, 0.4, 0.5, 0.8, 1, 1.2, 1.6, 2, 2.5, 4, 5, 8, 10, 20, 25, 40, 50, 100, 200, 500, 1000];
	return steps.find((s) => s >= value) || value;
}

function Slider({ label, value, shown, min, max, step, onChange, aria }) {
	return (
		<label className="block">
			<span className="flex items-baseline justify-between text-sm font-semibold text-ink">
				<span>{label}</span>
				<span className="font-serif text-base">{shown ?? value}</span>
			</span>
			<input type="range" min={min} max={max} step={step} value={value}
				onChange={(e) => onChange(parseFloat(e.target.value))}
				className="mt-2 w-full accent-teal" aria-label={aria} />
		</label>
	);
}

export default function PrecisionCalculator({ method = "mean", guess: g0, sd: sd0 = 1, unit = "", k: k0 = 2, shares: shares0 = [0.5, 0.5],
	model = "two_way", target: t0, half: h0 = false, n: n0 = 100, conf: c0 = 0.95, categories }) {
	const spec = METHODS[method] || METHODS.mean;
	const [guess, setGuess] = useState(g0 ?? (method === "mean" ? 0 : method === "alpha" ? 0.8 : 0.5));
	const [sd, setSd] = useState(sd0);
	const [k, setK] = useState(k0);
	const [shares, setShares] = useState(shares0.map((s) => Math.round(s * 1000) / 10));
	const [half, setHalf] = useState(h0);
	const [target, setTarget] = useState(t0 != null ? (h0 ? 2 * t0 : t0) : method === "mean" ? sd0 : 0.2); // always the full width
	const [n, setN] = useState(n0);
	const [conf, setConf] = useState(c0);
	const [hoverN, setHoverN] = useState(null);

	const args = { method, guess, sd, k, shares: shares.map((s) => Math.max(s, 0.1) / 100), model, conf };
	const deps = [guess, sd, k, shares.join(","), conf];
	const low = Math.max(MIN_N[method] || 2, method === "icc" || method === "alpha" ? 3 : 2);
	const width = (m) => { const [lo, hi] = interval({ ...args, n: m }); return hi - lo; };
	const needed = useMemo(() => sampleSizeForWidth({ ...args, target }), [target, ...deps]);
	const [lo, hi] = interval({ ...args, n });
	const now = hi - lo;

	const isMean = method === "mean";
	// Two decimals, so SD steps of 0.02 show (2.04, not 2); "1 hour", not "1 hours".
	const fmt = (v) => {
		if (!isMean) return noZero(v);
		const x = +v.toFixed(2);
		return `${x}${unit ? ` ${x === 1 && unit.endsWith("s") ? unit.slice(0, -1) : unit}` : ""}`;
	};
	const fmtWidth = (w) => (half ? `±${isMean ? fmt(w / 2) : noZero(w / 2)}` : isMean ? fmt(w) : noZero(w));
	const tMin = isMean ? sd * 0.04 : 0.02, tMax = isMean ? sd * 2 : method === "alpha" ? 0.4 : 0.8;
	const confLabel = `${Math.round(conf * 100)}%`;

	const xMax = niceMax(Math.max(n * 1.2, (needed || n) * 1.3, 20));
	const curve = useMemo(() => {
		const pts = [];
		const step = Math.max(1, Math.round(xMax / 150));
		for (let m = low; m <= xMax; m += step) pts.push([m, width(m)]);
		return pts;
	}, [xMax, ...deps]);
	// The y-axis starts where the curve has come down to a readable range.
	const yMax = niceWidth(Math.min(Math.max(target * 3, now * 1.2), curve.find(([m]) => m >= Math.max(low + 3, xMax * 0.05))?.[1] ?? target * 3));
	const x = (v) => M.left + (v / xMax) * PW;
	const y = (w) => M.top + (1 - Math.min(w, yMax) / yMax) * PH;
	const path = curve.filter(([, w]) => Number.isFinite(w)).map(([m, w], i) => `${i ? "L" : "M"}${x(m).toFixed(1)},${y(w).toFixed(1)}`).join(" ");
	const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * yMax);
	const xTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * xMax);
	const shown = (w) => (half ? w / 2 : w);
	const tick = (w) => (isMean ? +shown(w).toFixed(3) : noZero(shown(w), yMax < 0.2 ? 3 : 2));

	const onMove = (event) => {
		const box = event.currentTarget.getBoundingClientRect();
		const px = ((event.clientX - box.left) / box.width) * W;
		const m = Math.round(((px - M.left) / PW) * xMax);
		setHoverN(m >= low && m <= xMax ? m : null);
	};
	const hoverWidth = hoverN ? width(hoverN) : null;
	const symmetric = method === "mean" || method === "kappa"; // the others' intervals are lopsided

	const setShare = (i, v) => setShares(shares.map((s, j) => (j === i ? Math.max(1, Math.min(98, v)) : s)));
	const shareTotal = shares.reduce((s, x) => s + x, 0);
	const names = categories || shares.map((_, i) => `Category ${i + 1}`);

	const what = {
		mean: <>the mean</>,
		correlation: <><em className="font-serif">r</em></>,
		icc: <>a single-rater consistency ICC</>,
		alpha: <>alpha</>,
		kappa: <>κ</>,
		proportion: <>the proportion</>
	}[method];
	const setting = {
		mean: <>an SD of {fmt(sd)}</>,
		correlation: <>a correlation of {noZero(guess)}</>,
		icc: <>an ICC of {noZero(guess)} and {k} raters</>,
		alpha: <>an alpha of {noZero(guess)} and {k} items</>,
		kappa: <>a kappa of {noZero(guess)}</>,
		proportion: <>a proportion of {noZero(guess)}</>
	}[method];

	return (
		<div className="chart rounded-[3px] border border-gray-200 bg-white p-6">
			<div className="grid gap-5 sm:grid-cols-2">
				{isMean
					? <Slider label={<>Expected Standard Deviation (<em className="font-serif">s</em>)</>} value={sd} shown={fmt(sd)}
						min={+(sd0 / 5).toPrecision(2)} max={+(sd0 * 3).toPrecision(2)} step={+(sd0 / 100).toPrecision(1)} onChange={setSd} aria="Expected standard deviation" />
					: <Slider label={<>{spec.guessLabel}{spec.symbol && <> (<em className="font-serif">{spec.symbol}</em>)</>}</>} value={guess} shown={noZero(guess)}
						min={spec.min} max={spec.max} step={0.01} onChange={setGuess} aria={spec.guessLabel} />}
				<Slider label={half ? <>Target Half-Width (±)</> : <>Target Interval Width</>} value={target} shown={fmtWidth(target)}
					min={tMin} max={tMax} step={isMean ? +(sd0 / 200).toPrecision(1) : 0.01} onChange={setTarget} aria="Target interval width" />
				<Slider label={<>{spec.nLabel} (<em className="font-serif">n</em>)</>} value={n} min={Math.max(5, low)} max={Math.max(300, niceMax((needed || 0) * 1.1))} step={1}
					onChange={(v) => setN(Math.round(v))} aria="Sample size" />
				{spec.kLabel &&
					<label className="block text-sm">
						<span className="font-semibold text-ink">{spec.kLabel} (<em className="font-serif">k</em>)</span>
						<select value={k} onChange={(e) => setK(parseInt(e.target.value, 10))}
							className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2">
							{Array.from({ length: spec.kMax - 1 }, (_, i) => i + 2).map((v) => <option key={v} value={v}>{v}</option>)}
						</select>
					</label>}
				<label className="block text-sm">
					<span className="font-semibold text-ink">Confidence Level</span>
					<select value={conf} onChange={(e) => setConf(parseFloat(e.target.value))}
						className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2">
						<option value={0.9}>90%</option>
						<option value={0.95}>95%</option>
						<option value={0.99}>99%</option>
					</select>
				</label>
				<fieldset className="text-sm">
					<legend className="font-semibold text-ink">Target Is</legend>
					<div className="mt-2 flex gap-5">
						{[[false, "The whole width"], [true, "Half the width (±)"]].map(([value, text]) => (
							<label key={text} className="flex items-center gap-2">
								<input type="radio" name={`precision-half-${method}`} checked={half === value}
									onChange={() => setHalf(value)} className="accent-teal" />
								{text}
							</label>
						))}
					</div>
				</fieldset>
				{method === "kappa" &&
					<fieldset className="text-sm sm:col-span-2">
						<legend className="font-semibold text-ink">How Often Each Code Is Used (%)</legend>
						<div className="mt-2 flex flex-wrap gap-4">
							{shares.map((s, i) => (
								<label key={i} className="flex items-center gap-2">
									<span className="text-gray-700">{names[i]}</span>
									<input type="number" min="1" max="98" step="0.5" value={s} onChange={(e) => setShare(i, parseFloat(e.target.value || "0"))}
										className="w-16 rounded border border-gray-300 bg-white px-2 py-1" aria-label={`${names[i]} share`} />
								</label>
							))}
							{Math.abs(shareTotal - 100) > 0.01 && <span className="text-gray-500">(rescaled from {+shareTotal.toFixed(1)}% to 100%)</span>}
						</div>
					</fieldset>}
			</div>

			<div className="mt-6 flex flex-wrap items-end gap-x-8 gap-y-2 border-t border-gray-200 pt-5">
				<div>
					<div className="text-sm text-gray-600">Sample Size Needed</div>
					<div className="text-5xl font-bold tracking-tight text-ink">{needed ? needed.toLocaleString() : "20,000+"}</div>
				</div>
				<p className="max-w-md text-sm leading-relaxed text-gray-700">
					For a {confLabel} interval {!half ? `no wider than ${fmtWidth(target)}` : symmetric ? `of ${fmtWidth(target)}` : `with a total width of ${noZero(target)} (${fmtWidth(target)} on average; the interval isn't symmetric, so one side runs a little longer)`} around {what}, with {setting}, you need {needed ? <><span className="font-serif">{needed.toLocaleString()}</span> {spec.noun}</> : "more than 20,000"}.
					{method === "icc" && <> An absolute-agreement ICC usually needs more, especially with few raters or occasions; treat this as a reference, not a guaranteed minimum.</>}
				</p>
			</div>
			<p className="mt-3 text-sm text-gray-700">
				With <span className="font-serif">{n}</span> {spec.noun}, the interval would be about <strong>{fmtWidth(now)}</strong>{half ? "" : " wide"}
				{isMean ? "" : <> (from {noZero(lo)} to {noZero(hi)})</>}.
				{method === "kappa" && (hi > 1 || lo < -1) &&
					<> That runs past {hi > 1 ? "1, the highest" : "−1, the lowest"} kappa can be, a sign that with this few {spec.noun} the large-sample formula isn't trustworthy.</>}
			</p>

			<figure className="mt-5">
				<figcaption className="text-sm font-semibold text-ink">Interval {half ? "Half-Width" : "Width"} by Sample Size</figcaption>
				<svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" role="img"
					aria-label={`The expected ${confLabel} interval narrows as the sample grows; it reaches the target of ${fmtWidth(target)} at ${needed || "more than 20,000"} ${spec.noun}.`}
					onMouseMove={onMove} onMouseLeave={() => setHoverN(null)}>
					<defs>
						<clipPath id={`precision-plot-${method}`}><rect x={M.left} y={M.top - 2} width={PW} height={PH + 4} /></clipPath>
					</defs>
					{yTicks.map((w) => (
						<g key={w}>
							<line x1={M.left} x2={W - M.right} y1={y(w)} y2={y(w)} stroke={GRID} strokeWidth="1" />
							<text x={M.left - 8} y={y(w)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill={MUTED}>{half && w ? "±" : ""}{tick(w)}</text>
						</g>
					))}
					{target <= yMax && <>
						<line x1={M.left} x2={W - M.right} y1={y(target)} y2={y(target)} stroke={MUTED} strokeWidth="1" strokeDasharray="4 4" />
						<text x={W - M.right} y={y(target) - 6} textAnchor="end" fontSize="11" fill={MUTED}>Target</text>
					</>}
					{xTicks.map((m) => (
						<text key={m} x={x(m)} y={H - M.bottom + 18} textAnchor="middle" fontSize="11" fill={MUTED}>{m.toLocaleString()}</text>
					))}
					<text x={M.left + PW / 2} y={H - 4} textAnchor="middle" fontSize="11" fill={MUTED}>{spec.nLabel} (n)</text>

					<path d={path} fill="none" stroke={CHART_1} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" clipPath={`url(#precision-plot-${method})`} />
					{needed && needed <= xMax && <line x1={x(needed)} x2={x(needed)} y1={y(target)} y2={M.top + PH} stroke={CHART_1} strokeWidth="1" strokeDasharray="2 3" />}
					{n <= xMax && now <= yMax && <circle cx={x(n)} cy={y(now)} r="5" fill={CHART_1} stroke={PAPER} strokeWidth="2" />}

					{hoverN && (
						<g pointerEvents="none">
							<line x1={x(hoverN)} x2={x(hoverN)} y1={M.top} y2={M.top + PH} stroke={MUTED} strokeWidth="1" />
							{hoverWidth <= yMax && <circle cx={x(hoverN)} cy={y(hoverWidth)} r="4" fill={CHART_1} stroke={PAPER} strokeWidth="2" />}
							<g transform={`translate(${Math.min(x(hoverN) + 8, W - M.right - 130)},${M.top + 4})`}>
								<rect width="130" height="38" rx="3" fill={PAPER} stroke={GRID} />
								<text x="10" y="16" fontSize="11" fill={MUTED}>n = {hoverN}</text>
								<text x="10" y="31" fontSize="12" fontWeight="600" fill={INK}>{fmtWidth(hoverWidth)}{half ? "" : " wide"}</text>
							</g>
						</g>
					)}
				</svg>
			</figure>

			<p className="mt-3 text-xs text-gray-500">
				The width a study would report if its estimate came out at your guess; real studies land a little wider or narrower.{" "}
				{{
					mean: <>Calculated from the <em>t</em> interval, mean ± <em>t</em> × <em>s</em> ÷ √<em>n</em>, taking the sample SD to equal your guess; with a larger SD the interval widens in proportion.</>,
					correlation: <>Calculated from the Fisher <em>z</em> interval at your expected <em>r</em>.</>,
					icc: <>Calculated from the exact <em>F</em>-based interval for a single-rater consistency ICC ({model === "one_way" ? "one-way" : "two-way"} model), the interval Bonett's (2002) sample-size method is built for. An absolute-agreement ICC's interval is usually wider, especially with few raters, because it also has to estimate how much raters differ; to plan for one, simulate from expected target, rater and error variances.</>,
					alpha: <>Calculated from Feldt's <em>F</em>-based interval (Feldt, Woodruff &amp; Salih, 1987), taking the sample alpha to equal your guess.</>,
					kappa: <>Calculated from Fleiss, Cohen and Everitt's (1969) large-sample standard error, planned as Cantor (1996) does: κ ± <em>z</em> × √(<em>Q</em> ÷ <em>n</em>). Assumes both raters use the codes at these rates, and that disagreements fall across codes in proportion to how often they're used. It's a large-sample (Wald) interval, so it's least trustworthy with few cases, a rare code or kappa near 1.</>,
					proportion: <>Calculated from the Wilson score interval at your expected proportion.</>
				}[method]}
			</p>
		</div>
	);
}
